import { Formik, FormikErrors, FormikProps } from 'formik';
import { FormButton } from '../../../_core/form/button/form-button.component';
import { IRIFormProps } from '../../../_core/form/form.types';
import { IRequestGroup, ITemplateBaseDetails } from '../../../../lib/types';
import { IPersonalCompanyProps } from '../../../_core/core.types';
import { ICreateRequestGroupProps } from '../request-group.types';
import { EditRequestGroupFormFields, IEditRequestGroupFormFieldProps } from './edit-request-group-form-fields';
import { IMultiSelectOption } from '../../../_core/input/multiselect-input.component';
import { useMemo } from 'react';
import { getName } from '../../../../utils/name.utils';
import { getUrlValidationError } from '../../../../lib/url-validation';

interface IEditRequestGroupFormProps
  extends IRIFormProps<ICreateRequestGroupProps>,
    IPersonalCompanyProps,
    IEditRequestGroupFormFieldProps {
  currUserId: string;
  group?: IRequestGroup;
  template?: ITemplateBaseDetails;
}

export const EditRequestGroupForm = ({
  onSubmit,
  group,
  template,
  currUserId,
  ...rest
}: IEditRequestGroupFormProps) => {
  const validateForm = (values: ICreateRequestGroupProps) => {
    const errors: FormikErrors<ICreateRequestGroupProps> = {};
    if (!values.name) errors.name = 'Required';

    if (values.postSubmissionRedirectUrl) {
      const urlError = getUrlValidationError(values.postSubmissionRedirectUrl);
      if (urlError) {
        errors.postSubmissionRedirectUrl = urlError;
      }
    }

    return errors;
  };

  const clientsToIgnore = useMemo(
    () =>
      group?.requests
        ?.filter((r) => !r.deletedAt)
        .map((r) => r.assignedTo?._id)
        .filter((v) => v) as string[],
    [group?.requests]
  );

  return (
    <Formik
      initialValues={
        group
          ? ({
              name: group.name,
              assignedClients:
                group.assignedClients?.map(({ _id: value, ...rest }) => ({
                  label: getName(rest),
                  secondaryLabel: rest.user.email,
                  value
                })) ?? [],
              assignedStaff:
                group.assignedStaff?.map(({ _id: value, ...rest }) => ({
                  label: getName(rest),
                  secondaryLabel: rest.user.email,
                  value
                })) ?? [],
              canSelfServe: group.canSelfServe,
              deadline: group.deadline ?? '',
              expiration: group.expiration ?? '',
              notes: group.notes ?? '',
              postSubmissionRedirectUrl: group.postSubmissionRedirectUrl || '',
              requestType: group.requestType ? [{ label: group.requestType.type, value: group.requestType._id }] : []
            } as ICreateRequestGroupProps)
          : ({
              name: '',
              canSelfServe: false,
              assignedClients: [],
              assignedStaff: [],
              postSubmissionRedirectUrl: '',
              requestType: [],
              templateId: template
                ? ([
                    {
                      value: template._id,
                      label: template.title,
                      secondaryLabel: template.description
                    }
                  ] as IMultiSelectOption[])
                : []
            } as ICreateRequestGroupProps)
      }
      onSubmit={onSubmit}
      validate={validateForm}
      validateOnBlur={false}
    >
      {(props: FormikProps<ICreateRequestGroupProps>) => (
        <EditRequestGroupFormFields
          {...props}
          {...rest}
          clientsToIgnore={clientsToIgnore}
          createdById={group?.createdBy._id ?? currUserId}
          hasBeenCreated={!!group}
        >
          <FormButton loading={props.isSubmitting} text={group ? 'Update' : 'Create'} />
        </EditRequestGroupFormFields>
      )}
    </Formik>
  );
};
